import { BaseCommand } from '../ICommand';
import { getNodeMutationManager } from '../mutations/NodeMutationManager';
import type { Edge } from '../../models/scene';
import { useDiagramStore } from '../../../stores/diagramStore';
import { getProfileForDiagramType } from '../../data-profiles/profileRegistry';
import { resolveEdgeBinding, applyEntityFieldEdgeChange } from '../../../domains/data-binding/utils/edgeBinding';
import { getDataBinding } from '../../../domains/data-binding/services/BoundShapeEditInterceptor';
import { getGravityUserId } from '../../../hooks/useAuth';
import { readYjsValue } from '../../../lib/diagram/yjsCompat';
import type { IScenarioRecordable, CommandDeltaContribution } from '../../data-profiles/scenarios/ScenarioRecordable';
import { createEdgeId, createNodeId, createManagerId } from '../../data-profiles/scenarios/ScenarioDelta';

function isReportingEdge(edge: Edge): boolean {
  const t = edge.type as string;
  const relation = (edge.attrs as Record<string, unknown> | undefined)?.relation as string | undefined;
  return t === 'reporting' || t === 'reports_to' || relation === 'reporting' || relation === 'reports_to';
}

function isMatrixReportingUpdate(updates: Partial<Edge>): boolean {
  const type = updates.type as string | undefined;
  const relation = (updates.attrs as Record<string, unknown> | undefined)?.relation;
  return type === 'matrix' || type === 'matrix_reports' || relation === 'matrix_reports';
}

function isMatrixReportingEdge(edge: Edge): boolean {
  const type = edge.type as string;
  const relation = (edge.attrs as Record<string, unknown> | undefined)?.relation;
  return type === 'matrix' || type === 'matrix_reports' || relation === 'matrix_reports';
}

export class UpdateEdgeCommand extends BaseCommand implements IScenarioRecordable {
  readonly isScenarioRecordable = true as const;

  private edgeId: string;
  private updates: Partial<Edge>;
  private prevEdge: Edge | null = null;
  private nextEdge: Edge | null = null;
  private appliedUpdates: Partial<Edge> | null = null;

  constructor(edgeId: string, updates: Partial<Edge>) {
    super('Update Edge', `Update edge ${edgeId}`);
    this.edgeId = edgeId;
    this.updates = updates;
  }

  private syncReportingBindingTransition(previous: Edge | null, next: Edge | null): void {
    const candidate = next ?? previous;
    if (!candidate || !isReportingEdge(candidate)) return;

    const mutationManager = getNodeMutationManager();
    const doc = mutationManager.getDoc();
    if (!doc) return;

    const profile = getProfileForDiagramType('org');
    const bindingMapping = profile ? resolveEdgeBinding('reporting', profile) : undefined;
    if (bindingMapping?.pattern !== 'entity-field') return;

    const shapesMap = doc.getMap('shapes');
    const previousOwnerShapeId =
      previous && bindingMapping.fieldOwner === 'from' ? previous.from : previous?.to;
    const nextOwnerShapeId = next && bindingMapping.fieldOwner === 'from' ? next.from : next?.to;

    const previousOwnerShape = previousOwnerShapeId
      ? readYjsValue<Record<string, unknown> | undefined>(shapesMap.get(previousOwnerShapeId))
      : undefined;
    const nextOwnerShape = nextOwnerShapeId
      ? readYjsValue<Record<string, unknown> | undefined>(shapesMap.get(nextOwnerShapeId))
      : undefined;

    const previousOwnerBinding = previousOwnerShape ? getDataBinding(previousOwnerShape) : null;
    const nextOwnerBinding = nextOwnerShape ? getDataBinding(nextOwnerShape) : null;
    const sourceId = nextOwnerBinding?.sourceId ?? previousOwnerBinding?.sourceId;
    if (!sourceId) return;

    const userId = getGravityUserId() ?? 'unknown';

    // If ownership moved (from-node changed), clear the old owner's manager field first.
    if (
      previous &&
      next &&
      bindingMapping.fieldOwner === 'from' &&
      previous.from !== next.from
    ) {
      applyEntityFieldEdgeChange(doc, bindingMapping, previous.from, null, sourceId, userId);
    }

    if (next) {
      applyEntityFieldEdgeChange(doc, bindingMapping, next.from, next.to, sourceId, userId);
      return;
    }

    if (previous) {
      applyEntityFieldEdgeChange(doc, bindingMapping, previous.from, null, sourceId, userId);
    }
  }

  execute(): void {
    const mutationManager = getNodeMutationManager();
    const current = mutationManager.getEdge(this.edgeId);
    this.prevEdge = current ? { ...current, attrs: { ...(current.attrs || {}) } } as Edge : null;

    if (!current) return;

    // Deep-merge attrs to avoid dropping existing fields (e.g., color)
    const currentAttrs = (current.attrs || {}) as Record<string, unknown>;
    const incomingAttrs = (this.updates as any)?.attrs as Record<string, unknown> | undefined;
    const mergedAttrs = incomingAttrs ? { ...currentAttrs, ...incomingAttrs } : currentAttrs;
    const finalUpdates: Partial<Edge> = { ...this.updates, attrs: mergedAttrs as any };

    // Apply update via MutationManager with merged attrs
    mutationManager.updateEdge(this.edgeId, finalUpdates);
    this.appliedUpdates = finalUpdates;
    useDiagramStore.getState().updateEdge(this.edgeId, finalUpdates, true);

    const after = mutationManager.getEdge(this.edgeId);
    this.nextEdge = after ? { ...after, attrs: { ...(after.attrs || {}) } } as Edge : null;
    this.syncReportingBindingTransition(this.prevEdge, this.nextEdge);
  }

  undo(): void {
    if (!this.prevEdge) return;

    const mutationManager = getNodeMutationManager();
    // Restore previous essential fields
    const restoreUpdates = {
      type: this.prevEdge.type,
      from: this.prevEdge.from,
      to: this.prevEdge.to,
      attrs: this.prevEdge.attrs as any,
      fromAnchor: (this.prevEdge as any).fromAnchor,
      toAnchor: (this.prevEdge as any).toAnchor,
      fromOffset: (this.prevEdge as any).fromOffset,
      toOffset: (this.prevEdge as any).toOffset,
      fromAnchorLocked: (this.prevEdge as any).fromAnchorLocked,
      toAnchorLocked: (this.prevEdge as any).toAnchorLocked,
    } as Partial<Edge>;
    mutationManager.updateEdge(this.edgeId, restoreUpdates);
    useDiagramStore.getState().updateEdge(this.edgeId, restoreUpdates, true);
    this.syncReportingBindingTransition(this.nextEdge, this.prevEdge);
  }

  redo(): void {
    const mutationManager = getNodeMutationManager();
    const before = mutationManager.getEdge(this.edgeId);
    const beforeEdge = before ? { ...before, attrs: { ...(before.attrs || {}) } } as Edge : null;

    // Prefer reapplying the exact merged updates computed during execute
    if (this.appliedUpdates) {
      mutationManager.updateEdge(this.edgeId, this.appliedUpdates);
      useDiagramStore.getState().updateEdge(this.edgeId, this.appliedUpdates, true);
      const after = mutationManager.getEdge(this.edgeId);
      const afterEdge = after ? { ...after, attrs: { ...(after.attrs || {}) } } as Edge : null;
      this.syncReportingBindingTransition(beforeEdge, afterEdge);
      return;
    }
    // Fallbacks: use nextEdge snapshot if present, else original updates
    if (this.nextEdge) {
      const redoUpdates = {
        type: this.nextEdge.type,
        from: this.nextEdge.from,
        to: this.nextEdge.to,
        attrs: this.nextEdge.attrs as any,
        fromAnchor: (this.nextEdge as any).fromAnchor,
        toAnchor: (this.nextEdge as any).toAnchor,
        fromOffset: (this.nextEdge as any).fromOffset,
        toOffset: (this.nextEdge as any).toOffset,
        fromAnchorLocked: (this.nextEdge as any).fromAnchorLocked,
        toAnchorLocked: (this.nextEdge as any).toAnchorLocked,
      } as Partial<Edge>;
      mutationManager.updateEdge(this.edgeId, redoUpdates);
      useDiagramStore.getState().updateEdge(this.edgeId, redoUpdates, true);
      const after = mutationManager.getEdge(this.edgeId);
      const afterEdge = after ? { ...after, attrs: { ...(after.attrs || {}) } } as Edge : null;
      this.syncReportingBindingTransition(beforeEdge, afterEdge);
      return;
    }
    mutationManager.updateEdge(this.edgeId, this.updates);
    useDiagramStore.getState().updateEdge(this.edgeId, this.updates, true);
    const after = mutationManager.getEdge(this.edgeId);
    const afterEdge = after ? { ...after, attrs: { ...(after.attrs || {}) } } as Edge : null;
    this.syncReportingBindingTransition(beforeEdge, afterEdge);
  }

  toScenarioDelta(): CommandDeltaContribution {
    const edge = useDiagramStore.getState().edges.find(e => e.id === this.edgeId);
    if (!edge) return {};

    // The relationship subtype picker changes a dotted connector into a
    // matrix connector through this generic command. A matrix line is a
    // secondary relationship, not a primary-manager move, so it must stage a
    // typed edge addition for the org-chart scenario compiler.
    if (isMatrixReportingUpdate(this.updates) && !isMatrixReportingEdge(edge)) {
      const contribution: CommandDeltaContribution = {
        edgeAdditions: [{
          id: createEdgeId(edge.id),
          type: 'matrix_reports',
          from: createNodeId(this.updates.from ?? edge.from),
          to: createNodeId(this.updates.to ?? edge.to),
        }],
      };
      console.info('[MatrixScenarioDebug] matrix subtype delta serialized', {
        edgeId: this.edgeId,
        previousType: edge.type,
        previousRelation: (edge.attrs as Record<string, unknown> | undefined)?.relation,
        updates: this.updates,
        contribution,
      });
      return contribution;
    }

    if (!isReportingEdge(edge)) return {};

    const moves: CommandDeltaContribution['moves'] = [];

    // "to" changed = the manager changed for the person (edge.from)
    if (this.updates.to !== undefined && this.updates.to !== edge.to) {
      moves.push({
        nodeId: createNodeId(edge.from),
        oldManagerId: edge.to ? createManagerId(edge.to) : null,
        newManagerId: this.updates.to ? createManagerId(this.updates.to) : null,
      });
    }

    // "from" changed = different person now reports to this manager (edge.to)
    if (this.updates.from !== undefined && this.updates.from !== edge.from) {
      // Old person loses this manager
      moves.push({
        nodeId: createNodeId(edge.from),
        oldManagerId: edge.to ? createManagerId(edge.to) : null,
        newManagerId: null,
      });
      // New person gains this manager
      moves.push({
        nodeId: createNodeId(this.updates.from),
        oldManagerId: null,
        newManagerId: edge.to ? createManagerId(edge.to) : null,
      });
    }

    return { moves };
  }

  getUndoDelta(): CommandDeltaContribution {
    const edge = useDiagramStore.getState().edges.find(e => e.id === this.edgeId);
    if (!edge) return {};

    if (isMatrixReportingUpdate(this.updates) && !isMatrixReportingEdge(edge)) {
      return { edgeRemovals: [{ edgeId: createEdgeId(edge.id) }] };
    }

    if (!isReportingEdge(edge)) return {};

    const moves: CommandDeltaContribution['moves'] = [];

    // Reverse of "to" change
    if (this.updates.to !== undefined && this.updates.to !== edge.to) {
      moves.push({
        nodeId: createNodeId(edge.from),
        oldManagerId: this.updates.to ? createManagerId(this.updates.to) : null,
        newManagerId: edge.to ? createManagerId(edge.to) : null,
      });
    }

    // Reverse of "from" change
    if (this.updates.from !== undefined && this.updates.from !== edge.from) {
      // New person loses this manager (undo)
      moves.push({
        nodeId: createNodeId(this.updates.from),
        oldManagerId: edge.to ? createManagerId(edge.to) : null,
        newManagerId: null,
      });
      // Old person regains this manager (undo)
      moves.push({
        nodeId: createNodeId(edge.from),
        oldManagerId: null,
        newManagerId: edge.to ? createManagerId(edge.to) : null,
      });
    }

    return { moves };
  }
}
